import type { ComputedRef, Ref } from 'vue'
import type { SceneManager } from '@/three/SceneManager'
import type { HistoryManager } from '@/three/HistoryManager'
import type { useSceneStore, useSelectionStore } from '@/stores/scene'
import { getClickWorldPosition } from '@/three/DragHandler'
import { deepClone } from '@/utils/deepClone'
import { exclusiveConfirm, isExclusiveConfirmOpen } from '@/utils/exclusiveConfirm'
import { makeSceneObject } from '@/three/ObjectManager'
import { cloneSceneObjectAt } from '@/utils/cloneSceneObject'

type SceneStore = ReturnType<typeof useSceneStore>
type SelectionStore = ReturnType<typeof useSelectionStore>

export interface KeyboardShortcutsDeps {
  getSm: () => SceneManager | undefined
  sceneStore: SceneStore
  selectionStore: SelectionStore
  histManager: HistoryManager
  interactionReadonly: ComputedRef<boolean>
  currentFloorId: Ref<string>
  selectedId: Ref<string | null>
  selectedIds: Ref<string[]>
  clipboard: Ref<ReturnType<typeof makeSceneObject>[]>
  lastMouseInCanvas: Ref<{ clientX: number; clientY: number } | null>
  transformMode: Ref<'move' | 'rotate'>
  connectingFromId: Ref<string | null>
  /** 编辑中的设备连线 id（用于 Esc 关闭连线属性气泡） */
  selectedConnectionId?: Ref<string | null>
  measureToolActive: Ref<boolean>
  hasMeasureDraftPoint: () => boolean
  clearMeasureDraft: () => void
  stopRotateInteraction: () => void
  cancelConnecting: () => void
  /** Esc 时清除连线属性气泡选中 */
  clearConnectionSelection?: () => void
  notifyHistoryUi: () => void
  deselect: () => void
  doDelete: (id: string) => void
  doDuplicate: (id: string) => void
  emitSpaceKey: () => void
  /** 路径工具：Enter 完成草绘 */
  finishPathDraft?: () => void
  /** 路径工具：是否存在草绘点 */
  hasPathDraft?: () => boolean
  /** 路径工具：清空草绘点 */
  clearPathDraft?: () => void
  /** 路径工具：是否已启用 */
  pathToolActive?: Ref<boolean>
  /** 折线道路/围墙：是否存在草稿点 */
  hasLayoutDraft?: () => boolean
  clearLayoutDraft?: () => void
  finishLayoutDraft?: () => void
  hasPipeConnectDraft?: () => boolean
  clearPipeConnectDraft?: () => void
  /** 路径工具：Delete 删除选中的路径点 */
  deleteSelectedPathWaypoint?: () => boolean
  /** 折线对象：Delete 删除选中锚点 */
  deleteSelectedPolylineWaypoint?: () => boolean
  /** 仅选中路径时删除整条路径 */
  selectedPathId?: Ref<string | null>
  deleteSelectedPath?: () => void
  requestSave?: () => void
}

export function createKeyboardShortcuts(deps: KeyboardShortcutsDeps) {
  function onGlobalKey(e: KeyboardEvent) {
    if (isExclusiveConfirmOpen()) return
    if (deps.interactionReadonly.value) return
    if (e.key === 'Escape' && deps.selectedConnectionId?.value) {
      deps.clearConnectionSelection?.()
      return
    }
    if (e.key === 'Escape' && deps.measureToolActive.value) {
      if (deps.hasMeasureDraftPoint()) {
        deps.clearMeasureDraft()
        deps.getSm()?.markDirty()
      } else {
        deps.selectionStore.setMeasureToolActive(false)
      }
      return
    }
    if (
      e.key === 'Escape'
      && (
        deps.selectionStore.editorTool === 'roadStroke'
        || deps.selectionStore.editorTool === 'wallStroke'
        || deps.selectionStore.editorTool === 'riverStroke'
        || deps.selectionStore.editorTool === 'pipeStroke'
        || deps.selectionStore.editorTool === 'pipeConnect'
      )
    ) {
      if (deps.selectionStore.editorTool === 'pipeConnect') {
        if (deps.hasPipeConnectDraft?.()) deps.clearPipeConnectDraft?.()
        else deps.selectionStore.setEditorTool('none')
      } else if (deps.hasLayoutDraft?.()) deps.clearLayoutDraft?.()
      else deps.selectionStore.setEditorTool('none')
      deps.getSm()?.markDirty()
      return
    }
    if (e.key === 'Escape' && deps.pathToolActive?.value) {
      if (deps.hasPathDraft?.()) deps.clearPathDraft?.()
      else deps.selectionStore.setPathToolActive(false)
      return
    }
    if (e.key === 'Escape' && deps.connectingFromId.value) {
      deps.cancelConnecting()
      deps.connectingFromId.value = null
    }
    if (e.key === 'Escape' && deps.transformMode.value === 'rotate') {
      deps.transformMode.value = 'move'
      deps.stopRotateInteraction()
    }
    if (
      e.key === ' '
      && !(document.activeElement instanceof HTMLInputElement)
      && !(document.activeElement instanceof HTMLTextAreaElement)
    ) {
      e.preventDefault()
      deps.emitSpaceKey()
    }
  }

  async function onKeyDown(e: KeyboardEvent) {
    if (isExclusiveConfirmOpen()) return
    if (deps.interactionReadonly.value) return
    const isTyping =
      document.activeElement instanceof HTMLInputElement
      || document.activeElement instanceof HTMLTextAreaElement
      || document.activeElement instanceof HTMLSelectElement
    // e.key 理论上总是字符串，但部分浏览器扩展（翻译/密码管理器等）会派发缺 key 的合成 keydown，
    // 不加保护会在 .toLowerCase() 上抛出未捕获异常（onKeyDown 是 async，异常变成 unhandled rejection）
    const key = typeof e.key === 'string' ? e.key.toLowerCase() : ''
    if (e.ctrlKey && key === 'a') {
      if (isTyping) return
      e.preventDefault()
      const ids = deps.sceneStore
        .activeObjects()
        .filter(o => (o.floorId ?? 'f1') === deps.currentFloorId.value)
        .map(o => o.id)
      if (!ids.length) return
      deps.selectionStore.select(ids[0], { x: 0, y: 0 })
      ids.slice(1).forEach(sid => deps.selectionStore.multiSelect(sid, { x: 0, y: 0 }))
      return
    }

    const id = deps.selectedId.value
    if (key === 'r' && !e.ctrlKey && !e.altKey && !isTyping) {
      e.preventDefault()
      deps.transformMode.value = deps.transformMode.value === 'rotate' ? 'move' : 'rotate'
      if (deps.transformMode.value !== 'rotate') deps.stopRotateInteraction()
      return
    }

    // Enter 结束折线/路径草绘时没有任何对象被选中，不能被下面这道「必须有选中」的门槛拦掉
    const hasDraftInProgress = e.key === 'Enter' && (deps.hasLayoutDraft?.() || deps.hasPathDraft?.())
    if (!id && !deps.selectedPathId?.value && !hasDraftInProgress && !(e.ctrlKey && (e.key === 'z' || e.key === 'y' || e.key === 'v' || e.key === 'c'))) return

    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (!isTyping) {
        e.preventDefault()
        if (deps.deleteSelectedPolylineWaypoint?.()) return
        if (deps.deleteSelectedPathWaypoint?.()) return
        if (deps.selectedPathId?.value && !deps.selectedId.value) {
          deps.deleteSelectedPath?.()
          return
        }
        if (deps.selectedIds.value.length > 1) {
          const n = deps.selectedIds.value.length
          const ok = await exclusiveConfirm(`确定删除选中的 ${n} 个对象？`, '删除确认')
          if (!ok) return
          deps.histManager.push(deps.sceneStore.getEditorSnapshot())
          const ids = [...deps.selectedIds.value]
          deps.deselect()
          ids.forEach(sid => deps.sceneStore.removeObject(sid))
        } else if (id) {
          deps.doDelete(id)
        }
        return
      }
    }

    if (e.key === 'Enter' && !isTyping) {
      if (deps.hasLayoutDraft?.()) {
        deps.finishLayoutDraft?.()
        return
      }
      if (deps.hasPathDraft?.()) {
        deps.finishPathDraft?.()
      }
    }

    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      if (!id || isTyping) return
      e.preventDefault()
      const step = e.shiftKey ? 5 : 0.5
      const obj = deps.sceneStore.activeObjects().find(o => o.id === id)
      if (!obj) return
      let [x, y, z] = obj.position
      switch (e.key) {
        case 'ArrowUp': z -= step; break
        case 'ArrowDown': z += step; break
        case 'ArrowLeft': x -= step; break
        case 'ArrowRight': x += step; break
      }
      deps.histManager.push(deps.sceneStore.getEditorSnapshot())
      deps.sceneStore.updateObject(id, { position: [x, y, z] })
    }

    if (e.ctrlKey && e.key === 'c') {
      if (isTyping) return
      e.preventDefault()
      const ids = deps.selectedIds.value.length > 1 ? deps.selectedIds.value : (id ? [id] : [])
      const objs = deps.sceneStore.activeObjects()
      deps.clipboard.value = ids
        .map(sid => objs.find(o => o.id === sid))
        .filter((o): o is NonNullable<typeof o> => !!o)
        .map(o => deepClone(o))
    }

    if (e.ctrlKey && e.key === 'v') {
      if (isTyping) return
      e.preventDefault()
      if (!deps.clipboard.value.length) return
      deps.histManager.push(deps.sceneStore.getEditorSnapshot())
      const first = deps.clipboard.value[0]
      let basePos: [number, number, number]
      const sm = deps.getSm()
      const canvas = sm?.renderer?.domElement
      if (deps.lastMouseInCanvas.value && canvas && sm) {
        const floor = deps.sceneStore.activeFloors().find(f => f.id === deps.currentFloorId.value)
        const yElevation = floor?.elevation ?? 0
        const pos = getClickWorldPosition(deps.lastMouseInCanvas.value, canvas, sm.camera, { groundY: yElevation })
        basePos = pos ? [pos.x, yElevation, pos.z] : [first.position[0] + 2, first.position[1], first.position[2] + 2]
      } else {
        basePos = [first.position[0] + 2, first.position[1], first.position[2] + 2]
      }
      const dx = basePos[0] - first.position[0]
      const dz = basePos[2] - first.position[2]
      const floors = deps.sceneStore.activeFloors()
      const tgtFloorId = deps.currentFloorId.value
      const tgtElev = floors.find(f => f.id === tgtFloorId)?.elevation ?? 0
      const newObjs = deps.clipboard.value.map(src => {
        const srcElev = floors.find(f => f.id === (src.floorId ?? 'f1'))?.elevation ?? 0
        const newY = src.position[1] + (tgtElev - srcElev)
        const obj = cloneSceneObjectAt(src, [
          src.position[0] + dx,
          newY,
          src.position[2] + dz,
        ])
        obj.floorId = tgtFloorId
        return obj
      })
      newObjs.forEach(o => deps.sceneStore.addObject(o))
      if (newObjs.length === 1) {
        deps.selectionStore.select(newObjs[0].id, { x: 0, y: 0 })
      } else {
        deps.selectionStore.select(newObjs[0].id, { x: 0, y: 0 })
        newObjs.slice(1).forEach(o => deps.selectionStore.multiSelect(o.id, { x: 0, y: 0 }))
      }
    }

    if (e.ctrlKey && key === 'g') {
      e.preventDefault()
      if (e.shiftKey) {
        if (!deps.selectedIds.value.length) return
        deps.histManager.push(deps.sceneStore.getEditorSnapshot())
        deps.sceneStore.ungroupByObjectIds(deps.selectedIds.value)
        return
      }
      if (deps.selectedIds.value.length < 2) return
      deps.histManager.push(deps.sceneStore.getEditorSnapshot())
      deps.sceneStore.createGroup(deps.selectedIds.value)
      return
    }

    if (e.ctrlKey && e.key === 'z') {
      e.preventDefault()
      const result = deps.histManager.undo(deps.sceneStore.getEditorSnapshot())
      if (result) deps.sceneStore.applySnapshot(result)
      deps.notifyHistoryUi()
    }

    if (e.ctrlKey && e.key === 'y') {
      e.preventDefault()
      const result = deps.histManager.redo(deps.sceneStore.getEditorSnapshot())
      if (result) deps.sceneStore.applySnapshot(result)
      deps.notifyHistoryUi()
    }

    if (e.ctrlKey && e.key === 'd') {
      if (!id) return
      e.preventDefault()
      deps.doDuplicate(id)
    }

    if (e.ctrlKey && e.key === 's') {
      e.preventDefault()
      deps.requestSave?.()
    }
  }

  return { onKeyDown, onGlobalKey }
}
